import { Logger } from '@shared/lib/logger';
import { DEXIE_DB_NAME } from './db';

const logger = new Logger('app-key-guard');

/** Where the running app's key is remembered between visits. */
export const APP_KEY_STORAGE_KEY = 'app_key';

// Set once a reset has run for this key, so a browser that can't persist the
// new key (blocked storage) doesn't reset and reload forever.
const RESET_DONE_KEY = 'app_key_reset';
// The same marker in `window.name`, which survives a reload without relying
// on storage at all: whatever storage does, one tab resets at most once.
const RESET_DONE_WINDOW_NAME = 'cf-app-key-reset:';

function alreadyReset(appKey: string): boolean {
	if (window.name === RESET_DONE_WINDOW_NAME + appKey) {
		return true;
	}
	try {
		return sessionStorage.getItem(RESET_DONE_KEY) === appKey;
	} catch {
		return true;
	}
}

// Used where `indexedDB.databases()` is missing.
const KNOWN_DATABASES = [
	DEXIE_DB_NAME,
	'cf-cache',
	'cf-issue-index',
	'canvasflow_db',
	'canvasflow_cache',
	// The reader-event log (`@shared/events`' EVENT_LOG_DB_NAME): another
	// app's reading must not be sent as this one's.
	'cf-events',
];

// A delete waits on every open connection; don't let one hold up the boot.
const DELETE_TIMEOUT_MS = 2000;

/**
 * True when this origin last ran a *different* app (its stored app key isn't
 * this build's). A different key is a different publisher's app -- on
 * localhost, or a domain re-pointed at another tenant -- so nothing the
 * previous one stored (session, downloads, cached content) belongs here.
 *
 * Not the same as a release: a new `version` of the same app keeps the
 * reader's session and settings (see `preserve-on-deploy.ts`).
 */
export function hasAppChanged(appKey: string): boolean {
	try {
		const stored = localStorage.getItem(APP_KEY_STORAGE_KEY);
		if (!stored || stored === appKey) {
			return false;
		}
		return !alreadyReset(appKey);
	} catch {
		return false;
	}
}

/**
 * Wipes everything the previous app stored on this origin -- localStorage,
 * sessionStorage, every Cache Storage cache and every IndexedDB database --
 * and records `appKey` as the app now running. The caller reloads afterwards,
 * so every module starts from the empty state rather than from what it read
 * at import time.
 */
export async function resetForNewApp(appKey: string): Promise<void> {
	logger.warn(
		`Stored app key differs from this app's (${appKey}): clearing all stored data and reloading once`,
	);
	window.name = RESET_DONE_WINDOW_NAME + appKey;
	await Promise.allSettled([clearCaches(), clearDatabases()]);
	try {
		localStorage.clear();
		sessionStorage.clear();
		localStorage.setItem(APP_KEY_STORAGE_KEY, appKey);
		sessionStorage.setItem(RESET_DONE_KEY, appKey);
	} catch {
		// Storage unavailable: nothing more to clear.
	}
}

async function clearCaches(): Promise<void> {
	if (typeof caches === 'undefined') {
		return;
	}
	const names = await caches.keys();
	await Promise.allSettled(names.map((name) => caches.delete(name)));
}

async function clearDatabases(): Promise<void> {
	if (typeof indexedDB === 'undefined') {
		return;
	}
	let names = KNOWN_DATABASES;
	try {
		const listed = await indexedDB.databases?.();
		if (listed) {
			names = listed
				.map((database) => database.name)
				.filter((name): name is string => !!name);
		}
	} catch {
		// Fall back to the known names.
	}
	await Promise.allSettled(names.map(deleteDatabase));
}

function deleteDatabase(name: string): Promise<void> {
	return new Promise((resolve) => {
		const timer = setTimeout(resolve, DELETE_TIMEOUT_MS);
		const done = () => {
			clearTimeout(timer);
			resolve();
		};
		try {
			const request = indexedDB.deleteDatabase(name);
			request.onsuccess = done;
			request.onerror = done;
		} catch {
			done();
		}
	});
}
